import React, { useMemo } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  CardActionArea,
  styled,
  CardContent,
  Card,
  Button,
} from '@mui/material';
import { BRAND } from '../../../../config/brand';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import GridViewIcon from '@mui/icons-material/GridView';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CartProduct, InventoryCategory, FilteredProduct } from '../QuickSaleScreen';

// Styled Components
const CompactCard = styled(Card)(({ theme }) => ({
  height: '100%', // Grid item'a göre yükseklik
  display: 'flex',
  flexDirection: 'column',
}));

const CompactCardImage = styled('img')<{
  imagesizepercent?: number;
}>(({ theme, imagesizepercent = 100 }) => ({
  height: `${Math.min(80, 60 * (imagesizepercent / 100))}px`,
  objectFit: 'contain',
  padding: 0,
  display: 'block',
  width: '100%',
  margin: 0, // ✅ Tüm margin kaldırıldı
  flexShrink: 0,
}));

const CompactCardContent = styled(CardContent)<{
  fontsizepercent?: number;
}>(({ theme, fontsizepercent = 100 }) => ({
  textAlign: 'center',
  padding: '0 !important', // ✅ Tüm padding zorla kaldırıldı
  margin: 0,
  flexGrow: 0, // ✅ Genişleme kapatıldı - sadece içerik kadar yer kaplar
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start', // ✅ Üstten başla
  alignItems: 'center',
  '& .MuiTypography-caption': {
    fontSize: `calc(0.75rem * ${fontsizepercent / 100})`,
    lineHeight: 1.2,
  },
  '&:last-child': {
    paddingBottom: '0 !important',
  },
}));

const SortableProductCard: React.FC<{
  id: string;
  children: React.ReactNode;
  cardWidth: number;
  showImages: boolean;
}> = ({ id, children, cardWidth, showImages }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    cursor: 'grab',
    minWidth: `${cardWidth}px`,
    maxWidth: `${cardWidth}px`,
    width: `${cardWidth}px`,
    flexGrow: 0,
    flexShrink: 0,
    aspectRatio: showImages ? 'auto' : '1/1',
    padding: '4px',
    boxSizing: 'border-box' as const,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

interface ProductDisplayAreaProps {
  selectedCategory: string;
  handleCategoryChange: (event: React.SyntheticEvent, newValue: string) => void;
  productCategoryTabSize: number | 'small'; // 'small' mobil bottom sheet için
  displayableCategories: InventoryCategory[];
  activeDisplayWarehouseId: string | null; // For the warning message
  filteredProducts: FilteredProduct[];
  productCardSize: number;
  productCardFontSize: number;
  addToCart: (product: CartProduct, quantity?: number) => void;
  isQuantityModeActive: boolean;
  quantityInputValue: string;
  showProductImages: boolean; // Ürün resimlerinin gösterilip gösterilmeyeceği
  showProductPrices: boolean; // ✅ YENİ: Ürün fiyatlarının gösterilip gösterilmeyeceği
  productImageSize: number; // Kart içi resim boyutu
  isMobileBottomSheet?: boolean;
  categoryLayout?: 'horizontal' | 'vertical';
  categoryPosition?: 'left' | 'right';
  categoryFontSize?: number;
  isProductSortEditMode?: boolean;
  customProductOrder?: string[];
  onProductOrderChange?: (orderedIds: string[]) => void;
  onSaveProductOrder?: () => void;
  onResetProductOrder?: () => void;
  onExitProductSortEdit?: () => void;
}

const ProductDisplayArea: React.FC<ProductDisplayAreaProps> = ({
  selectedCategory,
  handleCategoryChange,
  productCategoryTabSize,
  displayableCategories,
  activeDisplayWarehouseId,
  filteredProducts,
  productCardSize,
  productCardFontSize,
  addToCart,
  isQuantityModeActive,
  quantityInputValue,
  showProductImages,
  showProductPrices,
  productImageSize,
  isMobileBottomSheet = false,
  categoryLayout = 'horizontal',
  categoryPosition = 'left',
  categoryFontSize = 100,
  isProductSortEditMode = false,
  customProductOrder,
  onProductOrderChange,
  onSaveProductOrder,
  onResetProductOrder,
  onExitProductSortEdit,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const sortedProducts = useMemo(() => {
    if (!customProductOrder || customProductOrder.length === 0) return filteredProducts;
    const orderMap = new Map(customProductOrder.map((id, idx) => [id, idx]));
    return [...filteredProducts].sort((a, b) => {
      const aIdx = orderMap.get(a.id.toString()) ?? Infinity;
      const bIdx = orderMap.get(b.id.toString()) ?? Infinity;
      return aIdx - bIdx;
    });
  }, [filteredProducts, customProductOrder]);

  const productIds = useMemo(() => sortedProducts.map(p => p.id.toString()), [sortedProducts]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onProductOrderChange) return;
    const oldIndex = productIds.indexOf(active.id as string);
    const newIndex = productIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(productIds, oldIndex, newIndex);
    onProductOrderChange(newOrder);
  };

  const CategoryButtons = ({ isVertical = false }: { isVertical?: boolean }) => {
    const baseSize = isMobileBottomSheet ? 1 : (productCategoryTabSize === 'small' ? 1 : Number(productCategoryTabSize) / 100);
    // ✅ YENİ: categoryFontSize ile yazı boyutu ayarlanabilir
    const fontSizeMultiplier = categoryFontSize / 100;
    const fontSize = isMobileBottomSheet ? '0.75rem' : `calc(0.95rem * ${baseSize * 1.3} * ${fontSizeMultiplier})`;
    const padding = isMobileBottomSheet ? '6px 10px' : `calc(8px * ${baseSize * 1.3}) calc(16px * ${baseSize * 1.3})`;
    const minHeight = isMobileBottomSheet ? 36 : `calc(44px * ${baseSize * 1.3})`;
    
    return (
      <>
        {displayableCategories.map((category) => {
          const isSelected = selectedCategory === category.id;
          
          return (
            <Button
              key={category.id}
              variant={isSelected ? "contained" : "outlined"}
              color={isSelected ? "primary" : "inherit"}
              onClick={(e) => handleCategoryChange(e, category.id)}
              sx={{
                textTransform: 'none',
                fontSize,
                fontWeight: isSelected ? 700 : 500,
                minHeight,
                padding,
                borderRadius: 2,
                whiteSpace: isVertical ? 'normal' : 'nowrap', // Dikeyde kelime kaydırma
                flexShrink: 0,
                boxShadow: isSelected ? 2 : 0,
                borderColor: isSelected ? 'primary.main' : 'grey.400',
                bgcolor: isSelected ? 'primary.main' : 'white',
                color: isSelected ? 'white' : 'text.primary',
                width: isVertical ? '100%' : 'auto', // Dikeyde tam genişlik
                justifyContent: isVertical ? 'flex-start' : 'center', // Dikeyde sola hizala
                textAlign: isVertical ? 'left' : 'center',
                '&:hover': {
                  bgcolor: isSelected ? 'primary.dark' : 'grey.100',
                  borderColor: isSelected ? 'primary.dark' : 'grey.500',
                },
              }}
            >
              {category.name}
            </Button>
          );
        })}
      </>
    );
  };

  // ✅ Dikey modda kategori ve ürünler yan yana
  const isVerticalLayout = categoryLayout === 'vertical' && !isMobileBottomSheet;
  
  return (
    <Grid item xs sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 300 }}>
      <Paper elevation={0} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.paper', position: 'relative' }}>
        
        {/* ✅ YATAY DÜZEN: Kategoriler üstte (mevcut davranış) */}
        {!isVerticalLayout && (
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: isMobileBottomSheet ? 0.5 : 1,
              p: isMobileBottomSheet ? 0.5 : 1,
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'grey.50',
              overflowX: 'auto',
              minHeight: isMobileBottomSheet ? 44 : 56,
            }}
          >
            <CategoryButtons isVertical={false} />
          </Box>
        )}
        
        {/* ✅ ANA İÇERİK ALANI - Dikey modda flex ile yan yana */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: isVerticalLayout 
            ? (categoryPosition === 'left' ? 'row' : 'row-reverse') // Sol veya sağ
            : 'column',
          overflow: 'hidden',
        }}>
          
          {/* ✅ DİKEY DÜZEN: Kategoriler yanda */}
          {isVerticalLayout && (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 0.5,
                p: 1,
                borderRight: categoryPosition === 'left' ? 1 : 0,
                borderLeft: categoryPosition === 'right' ? 1 : 0,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                overflowY: 'auto',
                minWidth: 140,
                maxWidth: 180,
                flexShrink: 0,
              }}
            >
              <CategoryButtons isVertical={true} />
            </Box>
          )}
          
          {/* ✅ ÜRÜNLER ALANI */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, bgcolor: '#e9ecef', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Düzenleme modu toolbar */}
          {isProductSortEditMode && (
            <Paper sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff3e0', border: '2px solid #ff9800', flexShrink: 0 }}>
              <GridViewIcon sx={{ color: '#e65100', fontSize: '1.2rem' }} />
              <Typography variant="body2" fontWeight="bold" sx={{ color: '#e65100', flex: 1, fontSize: '0.8rem' }}>
                Sıralama Düzenleme — Kartları sürükleyerek sırasını değiştirin
              </Typography>
              <Button variant="contained" color="success" size="small" startIcon={<SaveIcon />} onClick={onSaveProductOrder} sx={{ fontSize: '0.75rem' }}>
                Kaydet
              </Button>
              <Button variant="outlined" color="warning" size="small" startIcon={<RestartAltIcon />} onClick={onResetProductOrder} sx={{ fontSize: '0.75rem' }}>
                Sıfırla
              </Button>
              <Button variant="outlined" size="small" startIcon={<CloseIcon />} onClick={onExitProductSortEdit} sx={{ fontSize: '0.75rem' }}>
                İptal
              </Button>
            </Paper>
          )}
          {activeDisplayWarehouseId && displayableCategories.length <= 1 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Bu depo için ayarlanmış kategori bulunmamaktadır.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lütfen yönetici panelinden (Ayarlar &gt; Depo/Stok) ilgili depo için kategori ekleyiniz.
              </Typography>
            </Box>
          )}
          {/* Ürün Kartları */}
          {(() => {
            const productsToRender = sortedProducts;

            const renderProductCard = (product: FilteredProduct) => (
              <Grid
                item
                key={product.id.toString()}
                xs={isMobileBottomSheet ? 3 : undefined}
                sx={{
                  minWidth: isMobileBottomSheet ? 'auto' : `${productCardSize}px`,
                  maxWidth: isMobileBottomSheet ? '100%' : `${productCardSize}px`,
                  width: isMobileBottomSheet ? 'auto' : `${productCardSize}px`,
                  flexGrow: 0,
                  flexShrink: 0,
                  aspectRatio: showProductImages ? 'auto' : '1/1',
                  p: isMobileBottomSheet ? 0.25 : 0.5,
                }}
              >
                <CompactCard sx={{
                  width: '100%',
                  height: '100%',
                  boxShadow: productCardSize > 120 ? 3 : 1,
                  position: 'relative',
                  border: product.isBundle ? '2px solid' : undefined,
                  borderColor: product.isBundle ? (product.color || 'primary.main') : undefined,
                }}>
                  {product.isBundle && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 2,
                        bgcolor: product.color || 'primary.main',
                        color: '#fff',
                        fontSize: '0.55rem',
                        fontWeight: 'bold',
                        px: 0.6,
                        py: 0.1,
                        borderBottomRightRadius: 6,
                        letterSpacing: 0.3,
                      }}
                    >
                      PAKET
                    </Box>
                  )}
                  <CardActionArea 
                    onClick={() => {
                      const productToAdd: CartProduct = {
                        id: product.id,
                        name: product.name,
                        price: product.price
                      };
                      if (isQuantityModeActive && quantityInputValue) {
                        const quantity = parseFloat(quantityInputValue);
                        if (!isNaN(quantity) && quantity > 0) {
                          addToCart(productToAdd, quantity);
                        } else {
                          addToCart(productToAdd, 1);
                        }
                      } else {
                        addToCart(productToAdd, 1);
                      }
                    }}
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: showProductImages ? 'flex-start' : 'center',
                      alignItems: 'center',
                      p: 0.5,
                      height: showProductImages ? 'auto' : '100%',
                    }}
                  >
                    {showProductImages && (
                      <CompactCardImage
                        src={product.image}
                        alt={product.name}
                        imagesizepercent={productImageSize}
                      />
                    )}
                    <CompactCardContent 
                      fontsizepercent={productCardFontSize}
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'flex-start',
                        flexGrow: 0,
                        p: '0 !important',
                        m: 0,
                        width: '100%',
                      }}
                    >
                      <Typography 
                        variant={showProductImages ? "caption" : "body2"} 
                        component="div" 
                        align="center"
                        sx={{ 
                          fontWeight: isMobileBottomSheet ? 500 : (productCardSize > 120 ? 'bold' : 'medium'), 
                          lineHeight: 1.1,
                          m: 0,
                          p: 0,
                          fontSize: isMobileBottomSheet 
                            ? '0.6rem'
                            : (showProductImages 
                              ? `calc(${productCardSize > 120 ? '0.8' : '0.7'}rem * ${productCardFontSize / 100})`
                              : `calc(0.85rem * ${productCardFontSize / 100})`),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: isMobileBottomSheet ? 2 : (showProductImages && showProductPrices ? 2 : 3),
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                        }}
                      >
                        {product.name}
                      </Typography>
                      {showProductPrices && (
                        <Typography 
                          variant="caption" 
                          component="div" 
                          align="center"
                          sx={{ 
                            fontWeight: 'bold',
                            color: 'primary.main',
                            fontSize: isMobileBottomSheet ? '0.55rem' : `calc(0.7rem * ${productCardFontSize / 100})`,
                            m: 0,
                            p: 0,
                            flexShrink: 0,
                          }}
                        >
                          {new Intl.NumberFormat('tr-TR', {
                            style: 'currency',
                            currency: 'TRY',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(product.price)}
                        </Typography>
                      )}
                    </CompactCardContent>
                  </CardActionArea>
                </CompactCard>
              </Grid>
            );

            if (isProductSortEditMode && !isMobileBottomSheet) {
              return (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={productIds} strategy={rectSortingStrategy}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 0.5 }}>
                      {productsToRender.map((product) => (
                        <SortableProductCard
                          key={product.id.toString()}
                          id={product.id.toString()}
                          cardWidth={productCardSize}
                          showImages={showProductImages}
                        >
                          <CompactCard sx={{
                            width: '100%',
                            height: '100%',
                            boxShadow: productCardSize > 120 ? 3 : 1,
                            border: '2px dashed rgba(255,152,0,0.6)',
                          }}>
                            <CardActionArea
                              disabled
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: showProductImages ? 'flex-start' : 'center',
                                alignItems: 'center',
                                p: 0.5,
                                height: showProductImages ? 'auto' : '100%',
                              }}
                            >
                              {showProductImages && (
                                <CompactCardImage src={product.image} alt={product.name} imagesizepercent={productImageSize} />
                              )}
                              <CompactCardContent
                                fontsizepercent={productCardFontSize}
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', flexGrow: 0, p: '0 !important', m: 0, width: '100%' }}
                              >
                                <Typography
                                  variant={showProductImages ? "caption" : "body2"}
                                  component="div"
                                  align="center"
                                  sx={{
                                    fontWeight: productCardSize > 120 ? 'bold' : 'medium',
                                    lineHeight: 1.1, m: 0, p: 0,
                                    fontSize: showProductImages
                                      ? `calc(${productCardSize > 120 ? '0.8' : '0.7'}rem * ${productCardFontSize / 100})`
                                      : `calc(0.85rem * ${productCardFontSize / 100})`,
                                    overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                                    WebkitLineClamp: showProductImages && showProductPrices ? 2 : 3,
                                    WebkitBoxOrient: 'vertical', wordBreak: 'break-word',
                                  }}
                                >
                                  {product.name}
                                </Typography>
                                {showProductPrices && (
                                  <Typography variant="caption" component="div" align="center" sx={{
                                    fontWeight: 'bold', color: 'primary.main',
                                    fontSize: `calc(0.7rem * ${productCardFontSize / 100})`,
                                    m: 0, p: 0, flexShrink: 0,
                                  }}>
                                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(product.price)}
                                  </Typography>
                                )}
                              </CompactCardContent>
                            </CardActionArea>
                          </CompactCard>
                        </SortableProductCard>
                      ))}
                    </Box>
                  </SortableContext>
                </DndContext>
              );
            }

            return (
              <Grid container spacing={isMobileBottomSheet ? 0.5 : 1} sx={{ 
                p: isMobileBottomSheet ? 0.5 : 1,
                flexDirection: 'row',
                alignItems: 'stretch',
              }}>
                {productsToRender.map((product) => renderProductCard(product))}
                {productsToRender.length === 0 && (
                  <Grid item xs={12}>
                    <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary', fontSize: '0.9rem' }}>Uygun ürün bulunamadı.</Typography>
                  </Grid>
                )}
              </Grid>
            );
          })()}

          </Box>
        </Box>
        
        {/* ✅ Sağ alt köşede sabit logo - scroll ile hareket etmez */}
        {!isMobileBottomSheet && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <Box
              component="img"
              src={BRAND.logos.dark}
              alt={`${BRAND.name} Logo`}
              sx={{
                height: 24,
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
      </Paper>
    </Grid>
  );
};

export default ProductDisplayArea;
